// web/src/scene/MineScene.tsx
// M1.4 port of webview/src/scene/MineScene.tsx.
// Scene-composition root — reads Redux, owns modelBounds local state, wires
// props into Lighting + loaders.  MapTapHandler dispatches to Redux instead of
// calling sendToNative.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'

import { useAppSelector } from '../app/useAppSelector'
import { useAppDispatch } from '../app/useAppDispatch'
import { uiActions } from '@shared/slices/uiSlice'

import { Lighting } from './Lighting'
import { CameraController } from './CameraController'
import { useSceneRef } from './SceneRefContext'
import { GLBLoader } from './loaders/GLBLoader'
import { DXFLoader } from './loaders/DXFLoader'
import { RTLSOverlay } from './rtls/RTLSOverlay'
import { LocationMarkers } from './rtls/LocationMarkers'
import { AccessPointMarkers } from './rtls/AccessPointMarkers'
import { RescueChamberMarkers } from './rtls/RescueChamberMarkers'
import { TrailRenderer } from './rtls/TrailRenderer'
import { HeatmapLayer } from './rtls/HeatmapLayer'
import { AssetSelectionEffect } from './rtls/AssetSelectionEffect'
import { PlaybackAdvancer } from './rtls/PlaybackAdvancer'
import { useHistoryBounds } from './rtls/useHistoryBounds'
import { MarkerActionsProvider } from './rtls/useMarkerActions'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ModelBounds {
  center: { x: number; y: number; z: number }
  size: number
}

interface MineSceneProps {
  gpuTier: 'low' | 'high'
}

// ---------------------------------------------------------------------------
// MapTapHandler
// Ports webview's canvas-listener pattern; dispatches to Redux instead of
// calling sendToNative({ type: 'MAP_TAPPED', ... }).
// ---------------------------------------------------------------------------

function MapTapHandler() {
  const dispatch = useAppDispatch()
  const { scene, camera, gl } = useThree()

  const raycasterRef = useRef<THREE.Raycaster | null>(null)
  const pointerDownPos = useRef({ x: 0, y: 0 })

  // Lazily create raycaster (same pattern as webview)
  if (!raycasterRef.current) {
    raycasterRef.current = new THREE.Raycaster()
  }

  const handlePointerDown = useCallback((e: PointerEvent) => {
    pointerDownPos.current = { x: e.clientX, y: e.clientY }
  }, [])

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      // Ignore drags — only respond to taps (< 5px movement)
      const dx = e.clientX - pointerDownPos.current.x
      const dy = e.clientY - pointerDownPos.current.y
      if (Math.sqrt(dx * dx + dy * dy) > 5) return

      const rect = gl.domElement.getBoundingClientRect()
      const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1
      const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1

      const rc = raycasterRef.current!
      rc.setFromCamera({ x: ndcX, y: ndcY } as THREE.Vector2, camera)

      // Collect only map meshes. A mesh counts as "map" when one of its
      // ancestors is the tagged GLB root (userData.isMapModel) or the DXF
      // root (name 'DXF_Root'). This excludes RTLS marker planes/sprites and
      // any other overlay geometry, so the tap only fires on actual map
      // surface — not on markers or empty space.
      const isMapMesh = (o: THREE.Object3D): boolean => {
        for (let n: THREE.Object3D | null = o; n; n = n.parent) {
          if (n.userData?.isMapModel || n.name === 'DXF_Root') return true
        }
        return false
      }
      const meshes: THREE.Object3D[] = []
      scene.traverse((child: THREE.Object3D) => {
        if (
          (child as THREE.Mesh).isMesh &&
          !(child as any).isSprite &&
          isMapMesh(child)
        ) {
          meshes.push(child)
        }
      })

      const intersects = rc.intersectObjects(meshes, false)
      if (intersects.length > 0) {
        const p = intersects[0].point
        // Dispatch to Redux instead of sendToNative
        dispatch(
          uiActions.setTappedCoord({
            x: Math.round(p.x * 10) / 10,
            y: Math.round(p.y * 10) / 10,
            z: Math.round(p.z * 10) / 10,
          }),
        )
      }
    },
    [scene, camera, gl, dispatch],
  )

  useEffect(() => {
    const canvas = gl.domElement
    canvas.addEventListener('pointerdown', handlePointerDown)
    canvas.addEventListener('pointerup', handlePointerUp)
    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown)
      canvas.removeEventListener('pointerup', handlePointerUp)
    }
  }, [gl, handlePointerDown, handlePointerUp])

  return null
}

// ---------------------------------------------------------------------------
// MineScene
// ---------------------------------------------------------------------------

export function MineScene({ gpuTier }: MineSceneProps) {
  const [modelBounds, setModelBounds] = useState<ModelBounds | null>(null)
  const sceneRefHolder = useSceneRef()
  // One-shot guard — the first bounds event after load auto-frames the model
  // so users don't see a tiny map at the DB-stored camera position. Later
  // bounds updates (e.g., level-filter re-parses the GLB) leave the camera
  // alone so any manual zoom/pan is preserved.
  const autoFittedRef = useRef(false)

  // Redux selectors
  const showGrid = useAppSelector(s => s.viewer.showGrid)
  const performanceMode = useAppSelector(s => s.viewer.performanceMode)
  const viewConfig = useAppSelector(s => s.map.viewConfig)
  const playbackMode = useAppSelector(s => s.rtls.playbackMode)

  // Scope maxTs to the selected asset's trail when isolation is active so
  // the auto-play loop stops at the selected asset's last point, not the
  // fleet max. Shared with PlaybackBar's scrubber bounds.
  const { maxTs } = useHistoryBounds()

  // Shared "double-click any marker → fly camera here" handler. Provided
  // to every marker class via MarkerActionsProvider below so each class
  // behaves identically on dblclick without prop-drilling.
  const flyToPosition = useCallback((pos: { x: number; y: number; z: number }) => {
    sceneRefHolder.current?.camera.flyTo(pos, 150, 1.0)
  }, [sceneRefHolder])

  const markerActionsValue = useMemo(() => ({
    onMarkerDoubleClick: flyToPosition,
  }), [flyToPosition])

  // Note: the in-scene right-click "delete location" path was removed with
  // the dead onContextMenu handler. Deletes live in the RTLSPanel's
  // LocationsSection now. supabase + showToast + rtlsActions.removeLocation
  // remain imported elsewhere or have been pruned — see top of file.

  const handleBoundsReady = useCallback((box: THREE.Box3) => {
    const center = new THREE.Vector3()
    box.getCenter(center)
    const sizeVec = new THREE.Vector3()
    box.getSize(sizeVec)
    setModelBounds({
      center: { x: center.x, y: center.y, z: center.z },
      size: Math.max(sizeVec.x, sizeVec.y, sizeVec.z),
    })

    if (!autoFittedRef.current) {
      autoFittedRef.current = true
      sceneRefHolder.current?.camera.fitToModel(box)
    }
  }, [sceneRefHolder])

  return (
    <>
      <Lighting
        performanceMode={performanceMode}
        enableShadows={gpuTier === 'high'}
        modelBounds={modelBounds}
      />
      <CameraController />

      {showGrid &&
        viewConfig != null &&
        viewConfig.gridSize != null &&
        viewConfig.gridDivisions != null && (
          <gridHelper
            args={[
              viewConfig.gridSize,
              viewConfig.gridDivisions,
              '#333355',
              '#222244',
            ]}
            position={
              viewConfig.gridPosition != null
                ? (viewConfig.gridPosition as [number, number, number])
                : [0, 0, 0]
            }
            rotation={[Math.PI / 2, 0, 0]}
          />
        )}

      <GLBLoader onBoundsReady={handleBoundsReady} />
      <DXFLoader onBoundsReady={handleBoundsReady} />
      <MapTapHandler />

      {/* RTLS overlay + per-class markers. MarkerActionsProvider hands the
          shared double-click fly handler to every marker class below it. */}
      <MarkerActionsProvider value={markerActionsValue}>
        <RTLSOverlay showMarkers showLabels />
        <LocationMarkers />
        <AccessPointMarkers />
        <RescueChamberMarkers />
      </MarkerActionsProvider>

      <TrailRenderer />
      <HeatmapLayer />
      <AssetSelectionEffect />
      {playbackMode === 'playback' && <PlaybackAdvancer maxTs={maxTs} />}
    </>
  )
}
